import React from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { DATE_RANGE_OPTIONS, STRINGS } from '../utils/constants';

// Theme-aware styling for the PrimeReact MultiSelect — kept local so callers
// only pass intent (dark/light) instead of duplicating the style tree.
const buildMultiSelectStyle = (isDarkMode) => ({
  fontFamily: 'Cairo',
  fontSize: '15px',
  width: '100%',
  padding: 0,
  background: isDarkMode ? '#1e293b' : '#ffffff',
  color: isDarkMode ? '#f1f5f9' : '#0f172a',
  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
  borderRadius: '8px',
});

const buildMultiSelectPT = (isDarkMode) => ({
  root: { style: { background: isDarkMode ? '#1e293b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px' } },
  label: { style: { color: isDarkMode ? '#f1f5f9' : '#0f172a', fontFamily: 'Cairo' } },
  panel: { style: { background: isDarkMode ? '#0f172a' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } },
  item: { style: { color: isDarkMode ? '#f1f5f9' : '#0f172a', fontFamily: 'Cairo', background: 'transparent' } },
  checkboxIcon: { style: { color: '#3b82f6' } },
  checkbox: { style: { background: isDarkMode ? '#1e293b' : '#fff', border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #cbd5e1', borderRadius: '4px' } },
  token: { style: { background: isDarkMode ? 'rgba(59,130,246,0.2)' : '#e0f2fe', color: isDarkMode ? '#93c5fd' : '#0369a1', borderRadius: '6px', fontFamily: 'Cairo' } },
  tokenLabel: { style: { color: isDarkMode ? '#93c5fd' : '#0369a1' } },
  removeTokenIcon: { style: { color: isDarkMode ? '#93c5fd' : '#0369a1' } },
  trigger: { style: { color: isDarkMode ? '#94a3b8' : '#64748b' } },
  header: { style: { background: isDarkMode ? '#0f172a' : '#f8fafc', border: 'none', borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' } },
  filterInput: { style: { background: isDarkMode ? '#1e293b' : '#fff', color: isDarkMode ? '#f1f5f9' : '#0f172a', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', fontFamily: 'Cairo' } },
});

const ServiceOptionTemplate = (srv) => (
  <div className="flex align-items-center gap-1" style={{ fontFamily: 'Cairo', direction: 'rtl', paddingRight: '.5rem' }}>
    <span>{srv.service_name_ar || srv.service_name_en || `#${srv.ID_service}`}</span>
  </div>
);

const PaymentOptionTemplate = (pt) => (
  <div className="flex align-items-center gap-1" style={{ fontFamily: 'Cairo', direction: 'rtl', paddingRight: '.5rem' }}>
    <span>{pt.name_payment_type}</span>
  </div>
);

const FilterPanel = ({
  isDarkMode,
  dateRange,
  onDateRangeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  services,
  selectedServices,
  onServicesChange,
  loadingServices,
  paymentTypes,
  selectedPaymentTypes,
  onPaymentTypesChange,
  loadingPaymentTypes,
  onFetch,
  isFetching,
}) => {
  const msStyle = buildMultiSelectStyle(isDarkMode);
  const msPT = buildMultiSelectPT(isDarkMode);
  const emptyMsg = (
    <p style={{ fontFamily: 'Cairo', textAlign: 'center', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
      {STRINGS.noOptions}
    </p>
  );

  return (
    <div className="glass-card mb-4">
      <div className="filters-grid">
        <div className="filter-group">
          <label><i className="fa fa-calendar" /> {STRINGS.dateRange}</label>
          <select
            className="filter-select"
            value={dateRange}
            onChange={(e) => onDateRangeChange(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {dateRange === 'custom' && (
          <>
            <div className="filter-group">
              <label><i className="fa fa-calendar-plus-o" /> {STRINGS.startDate}</label>
              <input
                type="date"
                className="filter-input"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label><i className="fa fa-calendar-minus-o" /> {STRINGS.endDate}</label>
              <input
                type="date"
                className="filter-input"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="filter-group">
          <label><i className="fa fa-star" /> {STRINGS.serviceType}</label>
          <MultiSelect
            options={services}
            value={selectedServices}
            itemTemplate={ServiceOptionTemplate}
            onChange={(e) => onServicesChange(e.target.value)}
            optionLabel="service_name_ar"
            display="chip"
            placeholder={STRINGS.selectServicesPlaceholder}
            emptyMessage={emptyMsg}
            maxSelectedLabels={3}
            style={msStyle}
            pt={msPT}
            loading={loadingServices}
          />
        </div>

        <div className="filter-group">
          <label><i className="fa fa-credit-card" /> {STRINGS.paymentMethod}</label>
          <MultiSelect
            options={paymentTypes}
            value={selectedPaymentTypes}
            itemTemplate={PaymentOptionTemplate}
            onChange={(e) => onPaymentTypesChange(e.target.value)}
            optionLabel="name_payment_type"
            display="chip"
            placeholder={STRINGS.selectPaymentsPlaceholder}
            emptyMessage={emptyMsg}
            maxSelectedLabels={3}
            style={msStyle}
            pt={msPT}
            loading={loadingPaymentTypes}
          />
        </div>

        <div className="filter-group" style={{ justifyContent: 'flex-end' }}>
          <label style={{ opacity: 0 }}>{' '}</label>
          <button
            type="button"
            className="fetch-data-btn"
            onClick={onFetch}
            disabled={isFetching}
          >
            {isFetching ? (
              <><i className="fa fa-spinner fa-spin" /> {STRINGS.loadingReport}</>
            ) : (
              <><i className="fa fa-bar-chart" /> {STRINGS.fetchReport}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
